import apiClient from './apiClient';

class ReportService {
    /**
     * Get Credit Booking GST Report
     */
    async getCreditBookingGstReport(fromDate, toDate, customerId = null, branchId = null) {
        try {
            const response = await apiClient.post('/reports/credit-booking-gst', {
                from_date: fromDate,
                to_date: toDate,
                customer_id: customerId,
                branch_id: branchId || null
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching credit booking GST report:', error);
            throw error;
        }
    }

    /**
     * Export Credit Booking GST Report to Excel
     */
    async exportCreditBookingGstReport(fromDate, toDate, customerId = null, branchId = null) {
        try {
            const response = await apiClient.post(
                '/reports/credit-booking-gst/export',
                {
                    from_date: fromDate,
                    to_date: toDate,
                    customer_id: customerId,
                    branch_id: branchId || null
                },
                {
                    responseType: 'blob'
                }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `credit_booking_gst_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            return response;
        } catch (error) {
            console.error('Error exporting credit booking GST report:', error);
            throw error;
        }
    }

    /**
     * Get Instant Booking GST Report
     */
    async getInstantBookingGstReport(fromDate, toDate, gstNo = null, branchId = null, paymentType = null) {
        try {
            const response = await apiClient.post('/reports/instant-booking-gst', {
                from_date: fromDate,
                to_date: toDate,
                gst_no: gstNo,
                branch_id: branchId || null,
                payment_type: paymentType || null
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching instant booking GST report:', error);
            throw error;
        }
    }

    /**
     * Export Instant Booking GST Report to Excel
     */
    async exportInstantBookingGstReport(fromDate, toDate, gstNo = null, branchId = null, paymentType = null) {
        try {
            const response = await apiClient.post(
                '/reports/instant-booking-gst/export',
                {
                    from_date: fromDate,
                    to_date: toDate,
                    gst_no: gstNo,
                    branch_id: branchId || null,
                    payment_type: paymentType || null
                },
                {
                    responseType: 'blob'
                }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `instant_booking_gst_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            return response;
        } catch (error) {
            console.error('Error exporting instant booking GST report:', error);
            throw error;
        }
    }
}

/**
 * Read the JSON error body out of a failed blob request, so validation
 * messages from a download reach the user like any other error.
 */
const blobErrorMessage = async (error) => {
    const data = error.response?.data;
    if (data instanceof Blob) {
        try {
            const body = JSON.parse(await data.text());
            const firstFieldError = body.errors && Object.values(body.errors)[0]?.[0];
            return firstFieldError || body.message;
        } catch (e) {
            return null;
        }
    }
    return null;
};

/**
 * Business reports: catalogue, paged preview and CSV download.
 */
export const businessReportService = {
    async getCatalog() {
        const response = await apiClient.get('/reports/catalog');
        return response.data.data;
    },

    async run(key, filters, page = 1, perPage = 50) {
        const response = await apiClient.post(`/reports/run/${key}`, {
            ...filters,
            page,
            per_page: perPage
        });
        return response.data.data;
    },

    async download(key, filters) {
        try {
            const response = await apiClient.post(`/reports/run/${key}/export`, filters, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${key.replace(/-/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            const message = await blobErrorMessage(error);
            if (message) {
                error.message = message;
            }
            throw error;
        }
    }
};

const reportService = new ReportService();
export default reportService;
